/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import {
  useClickOutside,
  ZapierLogo,
  Icon,
  Link,
  Typography,
} from '@zapier/design-system';
import { labelStyles, withModifier } from '@zapier/style-encapsulation';
import { useEffect, useState } from 'react';
import {
  skipLinkHref as skipLinkHrefDefault,
  UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
  UNIVERSAL_TOPBAR_HEIGHT,
} from '../../../constants';
import { useUniversalNavContext } from '../../../hooks';
import SkipLink from '../../SkipLink';
import UniversalTopbarLink from '../UniversalTopbarLink';

import AccountActions from '../AccountActions';
import { emitUserInteractionEvent } from '../../../utils/avroEvents';

import type { TopbarProps } from '../types';

const Styles = labelStyles('UniversalTopbar', {
  root: [
    {
      display: 'flex',
      justifyContent: 'center',
      backgroundColor: `var(--zds-background-weaker, #FFFDF9)`,
      position: 'relative', // Needed for submenu dropdown
      borderBottom: `1px solid var(--zds-gray-warm-3, #ECE9DF)`,
    },
  ],
  header: ({ hasSidebar }: { hasSidebar: boolean }) => [
    css({
      width: '100%',
      height: UNIVERSAL_TOPBAR_CONDENSED_HEIGHT,
      padding: '0 22px 0 10px',
      display: 'grid',
      gridTemplateColumns: 'max-content auto max-content',
      alignItems: 'stretch',
      gap: '5px',
      backgroundColor: 'var(--zds-background-weaker, #FFFDF9)',

      '@media (min-width: 450px)': {
        height: UNIVERSAL_TOPBAR_HEIGHT,
        padding: '0 20px',
        gap: '0',
      },

      '@media (min-width: 1040px)': {
        padding: '0 40px',
      },
    }),
    hasSidebar &&
      withModifier(
        'has-sidebar',
        css({
          maxWidth: 'none',
          // Whenever there's a sidebar, and especially a sidebar toggle,
          // the toggle button itself takes up enough space to warrant
          // collapsing the padding on the left side.
          paddingLeft: '10px',
        })
      ),
  ],
  group: css({
    display: 'grid',
    gridAutoFlow: 'column',
    alignItems: 'center',
    justifySelf: 'flex-end',
    gap: '5px',

    '@media (min-width: 450px)': {
      gap: '17px',
    },

    'a[data-color=brand]': {
      height: '32px !important',
    },
  }),
  mainNavigationContent: [
    {
      display: 'flex',
      alignItems: 'stretch',
      marginLeft: 25,
    },
  ],

  integrationsCTA: css({
    display: 'flex',
    alignItems: 'center',
    borderRadius: `var(--zds-radius-small, 3px)`,
    height: 'var(--zds-size-36, 36px)',
    gap: '5px',
    color: 'var(--zds-gray-warm-9, #413735)',
    font: `var(--zds-body-1, 400 14px/24px ${Typography.base.fontFamily})`,
    padding: '0 10px',

    '&:hover, &:focus, &:focus-within': {
      backgroundColor: 'var(--zds-background-strongest, #ECE9DF)',
    },
  }),
  integrationsCTAText: css({
    '@media (max-width: 1200px)': {
      display: 'none',
    },
  }),
  linkButton: css({
    '& span': {
      font: `var(--zds-body-1, 400 14px/24px ${Typography.base.fontFamily})`,
      fontWeight: 'normal',
      color: 'var(--zds-gray-warm-10, #201515)',
    },
  }),
  navLinksList: (props: TopbarProps) => [
    {
      display: 'grid',
      ...(props.links && {
        // Accounts for static product tab whose hard-coded data won't be in props.links
        gridTemplateColumns: `repeat(${props.links.length + 1}, max-content)`,
      }),
    },
  ],
  logoLinksWrapper: {
    // This section has an element with position: absolute,
    // relative positioning here will define the bounds
    position: 'relative',
  },
  skipLink: {
    position: 'absolute',
    // Positions the SkipLink just below the logo
    top: '100%',
    whiteSpace: 'nowrap',
  },
});

const Links = (props: TopbarProps) => {
  const [activeMenu, setActiveMenu] = useState<string>('');
  const [activeElement, setActiveElement] = useState<Element | null>(null);

  // Clicking outside navigation area closes menu
  const [setClickOutsideNav] = useClickOutside({
    onClickOutside: () => setActiveMenu(''),
  });

  const toggleMegaMenu = (menuId: string) => {
    if (activeMenu === menuId) {
      if (activeElement instanceof HTMLElement) activeElement.focus();
      setActiveMenu('');
    } else {
      if (activeElement instanceof HTMLElement)
        setActiveElement(document.activeElement);
      setActiveMenu(menuId);
    }
  };

  const closeMenu = () => setActiveMenu('');

  const handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (activeElement instanceof HTMLElement) activeElement.focus();
      setActiveMenu('');
    }
  };

  // escape key closes menu
  useEffect(() => {
    document.addEventListener('keydown', handleEscape, { capture: false });

    return () => {
      document.removeEventListener('keydown', handleEscape, { capture: false });
    };
  });

  const links = props.links?.map((link) => (
    <UniversalTopbarLink
      ariaControls={link.text}
      isMenuOpen={activeMenu === link.text}
      key={link.text}
      navigationItem={link}
      onCloseMenu={closeMenu}
      position="navigation"
      toggleMenu={() => toggleMegaMenu(link.text)}
    >
      {link.text}
    </UniversalTopbarLink>
  ));
  if (!links) {
    return null;
  }
  return (
    <nav
      aria-label="Site Links"
      id="site-nav"
      ref={setClickOutsideNav}
      data-zds
    >
      <ul css={Styles.navLinksList(props)} data-zds>
        {links}
      </ul>
    </nav>
  );
};

/**
 * @private
 * Renders universal dynamic navigation top bar with Zapier logo, links,
 * and account actions.
 */
const UniversalTopbar = ({
  links = undefined,
  logoAriaLabel = 'Go to the Zapier Homepage',
  logoHref = '/',
  onLogoClick = () => {},
  selectedHref = '',
  shouldRenderLogo = true,
  size = 'medium',
  skipLinkHref = skipLinkHrefDefault,
  renderAccountMenu = (props: TopbarProps) => (
    <AccountActions
      selectedHref={props.selectedHref}
      shouldRenderSignIn={props.size !== 'small'}
      shouldRenderSignUp={true}
      size={props.size}
    />
  ),
  renderNotifications = () => null,
  ...otherProps
}: TopbarProps) => {
  const {
    currentUrl,
    getFullyQualifiedURL,
    shouldRenderCallToAction,
    topNavData,
  } = useUniversalNavContext();

  const isOnAuthPage = currentUrl === '/app/login' || currentUrl === '/sign-up';
  const isOnOnboardingPage = currentUrl.includes('/app/onboarding');

  const props = {
    links,
    logoAriaLabel,
    logoHref,
    onLogoClick,
    renderAccountMenu,
    renderNotifications,
    selectedHref,
    shouldRenderLogo,
    size,
    skipLinkHref,
    ...otherProps,
  } satisfies TopbarProps;

  const sidebar =
    !isOnAuthPage &&
    !isOnOnboardingPage &&
    props.renderSidebar &&
    props.renderSidebar(props);
  const logoSize = {
    width: 105,
    height: 28,
  };

  const [setClickOutsideNode] = useClickOutside({
    onClickOutside: (event: MouseEvent | TouchEvent) => {
      if (props.onClickOutside) props.onClickOutside(event);
    },
  });
  const callToActionUrl =
    getFullyQualifiedURL(
      `${topNavData.callToAction?.href}?demo_source=cs_nav_header_${currentUrl}`
    ) || '';

  return (
    <div css={Styles.root} ref={(node) => setClickOutsideNode(node)} data-zds>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
      <div
        css={Styles.header({
          hasSidebar: !!sidebar,
        })}
        data-testid="universalTopbar"
        onClick={props.onClick}
        data-zds
      >
        <div css={Styles.group} data-zds>
          {sidebar}
          {props.shouldRenderLogo && (
            <header css={Styles.logoLinksWrapper} data-zds>
              <a
                aria-label={props.logoAriaLabel}
                href={getFullyQualifiedURL(props.logoHref)}
                onClick={props.onLogoClick}
                data-zds
              >
                <ZapierLogo {...logoSize} />
              </a>
              <div css={Styles.skipLink} data-zds>
                <SkipLink href={props.skipLinkHref} />
              </div>
            </header>
          )}
        </div>
        <div css={Styles.mainNavigationContent} data-zds>
          {!isOnAuthPage && links && links.length > 0 && (
            <Links {...props} links={links} />
          )}
        </div>
        <div
          aria-label="Manage your account and explore apps"
          css={Styles.group}
          role="form"
          data-zds
        >
          <Link
            color={null}
            css={Styles.integrationsCTA}
            href={topNavData.integrationsCTA?.href}
            onClick={() => {
              emitUserInteractionEvent({
                interaction_name: 'call_to_action',
                interaction_raw_text: topNavData.integrationsCTA?.text,
                destination_href: topNavData.integrationsCTA?.href,
                event_action: 'click',
                option_selected: 'topbar_integrations_cta',
                experiment_flag: true,
                experiment_name: 'content-platforms-explore-integrations-cta',
              });
            }}
          >
            {topNavData.integrationsCTA && (
              <Icon name={topNavData.integrationsCTA?.icon} size={20}></Icon>
            )}
            <span css={Styles.integrationsCTAText} data-zds>
              {topNavData.integrationsCTA?.text}
            </span>
          </Link>

          {topNavData.callToAction &&
            props.size !== 'small' &&
            shouldRenderCallToAction && (
              <UniversalTopbarLink
                navigationItem={{
                  variant: 'link',
                  ...topNavData.callToAction,
                  href: callToActionUrl,
                }}
                position="actions-menu"
                isButton={false}
              >
                {topNavData.callToAction.text}
              </UniversalTopbarLink>
            )}

          {props.renderAccountMenu(props)}
        </div>
      </div>
    </div>
  );
};

export default UniversalTopbar;
